import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowLeft, ArrowRight, Gamepad2, Users, Lock, Settings, Bot } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSocket } from '../hooks/useSocket';
import GameSelectionCarousel from './GameSelectionCarousel';
import BotSelectionCarousel from './BotSelectionCarousel';

const GameSetupModal = ({ onClose }) => {
  const { t } = useTranslation(['gameCreation', 'common', 'bots']);
  const { createGame, socket } = useSocket();

  const [currentStep, setCurrentStep] = useState('game-selection');
  const [selectedGame, setSelectedGame] = useState(null);
  const [selectedBots, setSelectedBots] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm({
    defaultValues: {
      name: '',
      maxPlayers: 4,
      isPrivate: false,
      password: '',
      settings: {}
    }
  });

  const watchedMaxPlayers = watch('maxPlayers');
  const watchedIsPrivate = watch('isPrivate');

  // Steps configuration
  const steps = [
    { id: 'game-selection', title: t('carousel.title', { ns: 'gameCreation' }) },
    { id: 'setup', title: t('setup.title', { ns: 'gameCreation' }) },
    { id: 'confirmation', title: t('actions.confirm', { ns: 'gameCreation' }) }
  ];

  const currentStepIndex = steps.findIndex(step => step.id === currentStep);

  // Listen for game creation success
  useEffect(() => {
    if (socket) {
      const handleGameCreated = () => {
        setIsLoading(false);
        onClose();
      };

      socket.on('gameCreated', handleGameCreated);

      return () => {
        socket.off('gameCreated', handleGameCreated);
      };
    }
  }, [socket, onClose]);

  // Update max players and per-game settings when game changes
  useEffect(() => {
    if (selectedGame) {
      setValue('maxPlayers', selectedGame.maxPlayers || 4);
      setValue('name', `${selectedGame.displayName} Game`);
      // Reset settings to the selected game's own defaults so a previous
      // selection's settings never leak into another game
      if (selectedGame.gridSize) {
        setValue('settings', { gridSize: selectedGame.gridSize.default });
      } else if (selectedGame.episodes) {
        setValue('settings', { episodeId: selectedGame.episodes.default });
      } else {
        setValue('settings', {});
      }
    }
  }, [selectedGame, setValue]);

  // Calculate available slots for bots
  const humanPlayers = 1; // The creating player
  const maxBotsAllowed = Math.min(
    selectedGame?.botConfig?.maxBots || 0,
    Math.max(0, watchedMaxPlayers - humanPlayers)
  );

  const handleNext = () => {
    if (currentStep === 'game-selection' && selectedGame) {
      setCurrentStep('setup');
    } else if (currentStep === 'setup') {
      setCurrentStep('confirmation');
    }
  };

  const handleBack = () => {
    if (currentStep === 'setup') {
      setCurrentStep('game-selection');
    } else if (currentStep === 'confirmation') {
      setCurrentStep('setup');
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 'game-selection':
        return selectedGame !== null;
      case 'setup':
        return selectedGame && watchedMaxPlayers >= (selectedGame.minPlayers || 2);
      case 'confirmation':
        return true;
      default:
        return false;
    }
  };

  const onSubmit = async (data) => {
    if (!selectedGame) return;

    setIsLoading(true);

    try {
      const gameData = {
        ...data,
        type: selectedGame.name,
        selectedBots: selectedBots.map(bot => ({
          id: bot.id,
          name: bot.name,
          difficulty: bot.difficulty,
          personality: bot.personality
        }))
      };

      createGame(gameData);
    } catch (error) {
      console.error('Failed to create game:', error);
      setIsLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 'game-selection':
        return (
          <GameSelectionCarousel
            onGameSelect={setSelectedGame}
            selectedGame={selectedGame}
            onConfirm={handleNext}
          />
        );

      case 'setup':
        return (
          <div className="space-y-6">
            {/* Basic Settings */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Settings className="w-5 h-5" />
                {t('setup.subtitle', { ns: 'gameCreation' })}
              </h3>

              {/* Game Name */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t('gameName', { ns: 'game' })}
                </label>
                <input
                  {...register('name', { required: t('validation.selectGame', { ns: 'gameCreation' }) })}
                  type="text"
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={`${selectedGame?.displayName || 'My'} Game`}
                />
                {errors.name && (
                  <p className="text-red-400 text-sm mt-1">{errors.name.message}</p>
                )}
              </div>

              {/* Player Count */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t('setup.players.count', { ns: 'gameCreation' })}
                </label>
                <div className="relative">
                  <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    {...register('maxPlayers', {
                      required: t('maxPlayersRequired', { ns: 'game' }),
                      min: {
                        value: selectedGame?.minPlayers || 2,
                        message: t('validation.minPlayers', {
                          ns: 'gameCreation',
                          min: selectedGame?.minPlayers || 2
                        })
                      },
                      max: {
                        value: selectedGame?.maxPlayers || 16,
                        message: t('validation.maxPlayers', {
                          ns: 'gameCreation',
                          max: selectedGame?.maxPlayers || 16
                        })
                      }
                    })}
                    type="number"
                    min={selectedGame?.minPlayers || 2}
                    max={selectedGame?.maxPlayers || 16}
                    className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                {errors.maxPlayers && (
                  <p className="text-red-400 text-sm mt-1">{errors.maxPlayers.message}</p>
                )}
                <p className="text-gray-400 text-sm mt-1">
                  {t('setup.players.totalSlots', { ns: 'gameCreation', total: watchedMaxPlayers })}
                </p>
              </div>

              {/* Grid Size — only for games whose registry entry declares a
                  gridSize schema (e.g. dots-and-boxes) */}
              {selectedGame?.gridSize?.options?.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {t('setup.gridSize.label', { ns: 'gameCreation' })}
                  </label>
                  <select
                    {...register('settings.gridSize', { valueAsNumber: true })}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {selectedGame.gridSize.options.map(size => (
                      <option key={size} value={size}>
                        {size} × {size}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Episode — only for games whose registry entry declares an
                  episodes schema (Bible Match n Tell) */}
              {selectedGame?.episodes?.options?.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {t('setup.episode.label', { ns: 'gameCreation' })}
                  </label>
                  <select
                    {...register('settings.episodeId')}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {selectedGame.episodes.options.map(episode => (
                      <option key={episode.id} value={episode.id}>
                        {episode.displayName}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* Bot Selection */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Bot className="w-5 h-5 text-blue-400" />
                <h3 className="text-lg font-semibold text-white">
                  {t('setup.bots.title', { ns: 'gameCreation' })}
                </h3>
              </div>

              {selectedGame?.supportsBots ? (
                <BotSelectionCarousel
                  gameType={selectedGame.name}
                  maxBots={maxBotsAllowed}
                  selectedBots={selectedBots}
                  onBotsChange={setSelectedBots}
                />
              ) : (
                <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                  <p className="text-gray-400 text-sm">
                    {t('setup.bots.notSupported', { ns: 'gameCreation' })}
                  </p>
                </div>
              )}
            </div>

            {/* Privacy Settings */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Lock className="w-5 h-5" />
                {t('setup.privacy.title', { ns: 'gameCreation' })}
              </h3>

              <div className="flex items-center gap-3">
                <input
                  {...register('isPrivate')}
                  type="checkbox"
                  id="isPrivate"
                  className="w-4 h-4 text-blue-600 bg-gray-800 border-gray-600 rounded focus:ring-blue-500 focus:ring-2"
                />
                <label htmlFor="isPrivate" className="text-gray-300 flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  {t('setup.privacy.private', { ns: 'gameCreation' })}
                </label>
              </div>

              {watchedIsPrivate && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {t('setup.privacy.password', { ns: 'gameCreation' })}
                  </label>
                  <input
                    {...register('password')}
                    type="password"
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={t('setup.privacy.passwordPlaceholder', { ns: 'gameCreation' })}
                  />
                </motion.div>
              )}
            </div>
          </div>
        );

      case 'confirmation':
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-white">
              {t('confirmation.title', { ns: 'gameCreation' })}
            </h3>

            {/* Game Summary */}
            <div className="space-y-4">
              <div className="p-4 bg-gray-800/50 rounded-lg">
                <h4 className="text-white font-medium mb-2">{selectedGame?.displayName}</h4>
                <p className="text-gray-400 text-sm mb-3">{selectedGame?.description}</p>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-400">{t('confirmation.gameName', { ns: 'gameCreation' })}</span>
                    <span className="text-white ml-2">{watch('name')}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">{t('confirmation.maxPlayers', { ns: 'gameCreation' })}</span>
                    <span className="text-white ml-2">{watchedMaxPlayers}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">{t('confirmation.privacy', { ns: 'gameCreation' })}</span>
                    <span className="text-white ml-2">
                      {watchedIsPrivate
                        ? t('confirmation.private', { ns: 'gameCreation' })
                        : t('confirmation.public', { ns: 'gameCreation' })}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-400">{t('confirmation.bots', { ns: 'gameCreation' })}</span>
                    <span className="text-white ml-2">{selectedBots.length}</span>
                  </div>
                  {selectedGame?.gridSize && (
                    <div>
                      <span className="text-gray-400">{t('confirmation.gridSize', { ns: 'gameCreation' })}</span>
                      <span className="text-white ml-2">
                        {watch('settings.gridSize')} × {watch('settings.gridSize')}
                      </span>
                    </div>
                  )}
                  {selectedGame?.episodes && (
                    <div>
                      <span className="text-gray-400">{t('confirmation.episode', { ns: 'gameCreation' })}</span>
                      <span className="text-white ml-2">
                        {selectedGame.episodes.options.find(e => e.id === watch('settings.episodeId'))?.displayName}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Selected Bots Summary */}
              {selectedBots.length > 0 && (
                <div className="p-4 bg-blue-600/10 border border-blue-600/20 rounded-lg">
                  <h5 className="text-blue-400 font-medium mb-2">{t('confirmation.selectedBots', { ns: 'gameCreation' })}</h5>
                  <div className="space-y-2">
                    {selectedBots.map(bot => (
                      <div key={bot.id} className="flex items-center justify-between text-sm">
                        <span className="text-white">{bot.name}</span>
                        <span className="text-gray-400 capitalize">
                          {t(`selection.difficulty.${bot.difficulty}`, { ns: 'bots' })}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/85 backdrop-blur-md flex items-center justify-center p-4 z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="bg-gray-900/95 backdrop-blur-xl rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-gray-600"
      >
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <Gamepad2 className="w-6 h-6 text-blue-400" />
              <div>
                <h2 className="text-2xl font-bold text-white">
                  {steps[currentStepIndex]?.title}
                </h2>
                <div className="flex items-center gap-2 mt-1">
                  {steps.map((step, index) => (
                    <div
                      key={step.id}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        index === currentStepIndex
                          ? 'bg-blue-600'
                          : index < currentStepIndex
                          ? 'bg-green-600'
                          : 'bg-gray-600'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="mb-6">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                {renderStepContent()}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-gray-700">
            {currentStep !== 'game-selection' && (
              <button
                type="button"
                onClick={handleBack}
                disabled={isLoading}
                className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-600 text-white rounded-lg transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                {t('actions.back', { ns: 'gameCreation' })}
              </button>
            )}

            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-600 text-white rounded-lg transition-colors"
            >
              {t('actions.cancel', { ns: 'gameCreation' })}
            </button>

            <div className="flex-1" />

            {/* On game-selection the card's "Select This Game" button advances the step */}
            {currentStep === 'setup' && (
              <button
                type="button"
                onClick={handleNext}
                disabled={!canProceed() || isLoading}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white rounded-lg transition-colors"
              >
                {t('actions.next', { ns: 'gameCreation' })}
                <ArrowRight className="w-4 h-4" />
              </button>
            )}
            {currentStep === 'confirmation' && (
              <button
                onClick={handleSubmit(onSubmit)}
                disabled={isLoading}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white rounded-lg transition-colors"
              >
                {isLoading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  t('actions.create', { ns: 'gameCreation' })
                )}
              </button>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default GameSetupModal;